import React, { useState, useMemo } from 'react';
import {
  Drawer,
  Button,
  List,
  Input,
  Skeleton,
  Typography,
  Space,
  Popconfirm,
  Tag,
  Switch,
  Radio,
  Modal,
  Tooltip,
} from 'antd';
import {
  DeleteOutlined,
  PlusOutlined,
  InfoCircleOutlined,
  ScheduleOutlined,
} from '@ant-design/icons';
import { useGetAnalysisTemplates } from './hooks/useGetAnalysisTemplates';
import { useCreateAnalysisTemplate } from './hooks/useCreateAnalysisTemplate';
import { useUpdateAnalysisTemplate } from './hooks/useUpdateAnalysisTemplate';
import { useDeleteAnalysisTemplate } from './hooks/useDeleteAnalysisTemplate';
import useCanManageSystemPrompt from '../SystemPrompt/hooks/useCanManageSysPrompt';
import TagInput from './TagInput';
import moment from 'moment';
import { canSeeDeepModeFeatures } from '../utils/deepModeAccess';
import ScheduleAnalysisDrawer from '../ScheduleSavedAnalysis/ScheduleAnalysisDrawer';

const { Title, Text } = Typography;
const { TextArea } = Input;

interface AnalysisTemplate {
  id: number;
  customer_id: number;
  product: string;
  created_by: number;
  visibility: string;
  query: string;
  extra_instructions: string;
  deep_mode: boolean;
  tags: string[];
  updated_at: string;
  created_at: string;
}

const AnalysisListItem = ({ item, onClick, onDelete, onSchedule, selected }: any) => {
  const queryPreview = item.query.length > 50 ? item.query.substring(0, 50) + '...' : item.query;
  const canSeeDeepMode = canSeeDeepModeFeatures();

  const getVisibilityDisplay = (visibility: string) => {
    switch (visibility) {
      case 'private':
        return { color: 'green', label: 'Private' };
      case 'team':
        return { color: 'orange', label: 'Team' };
      case 'recommended':
        return { color: 'purple', label: 'Recommended' };
      default:
        return { color: 'default', label: visibility };
    }
  };

  const visibilityDisplay = getVisibilityDisplay(item.visibility);

  return (
    <List.Item
      onClick={() => onClick(item)}
      style={{
        cursor: 'pointer',
        backgroundColor: selected ? '#f0f5ff' : 'transparent',
        borderRadius: 4,
        padding: '12px',
        marginBottom: 4,
      }}
    >
      <div style={{ flex: 1 }}>
        <div style={{ marginBottom: 4 }}>
          <Text strong>{queryPreview}</Text>
        </div>
        <div style={{ marginBottom: 4, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {canSeeDeepMode && item.deep_mode && <Tag color="blue">Deep Analysis</Tag>}
          <Tag color={visibilityDisplay.color}>{visibilityDisplay.label}</Tag>
          {item.tags &&
            item.tags.map((tag) => (
              <Tag key={tag} color="default">
                {tag}
              </Tag>
            ))}
        </div>
        <div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Updated {moment(item.updated_at).format('MMM D, YYYY')}
          </Text>
        </div>
      </div>
      <Space size={4} onClick={(e) => e.stopPropagation()}>
        <Button
          icon={<ScheduleOutlined />}
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onSchedule(item);
          }}
        />
        <Popconfirm
          title="Delete this analysis?"
          onConfirm={(e) => {
            e?.stopPropagation();
            onDelete(item.id);
          }}
          okText="Yes"
          cancelText="No"
          onCancel={(e) => e?.stopPropagation()}
        >
          <Button
            icon={<DeleteOutlined />}
            size="small"
            danger
            onClick={(e) => e.stopPropagation()}
          />
        </Popconfirm>
      </Space>
    </List.Item>
  );
};

const EditForm = ({ template, onCancel, onSave, isSaving, isCreateMode }: any) => {
  const [query, setQuery] = useState(template?.query || '');
  const [extraInstructions, setExtraInstructions] = useState(template?.extra_instructions || '');
  const [deepMode, setDeepMode] = useState(template?.deep_mode || false);
  const [visibility, setVisibility] = useState(template?.visibility || 'private');
  const [tags, setTags] = useState<string[]>(template?.tags || []);
  const [errors, setErrors] = useState<{ query?: string; extraInstructions?: string }>({});
  const canManageSysPrompt = useCanManageSystemPrompt();
  const canSeeDeepMode = canSeeDeepModeFeatures();

  const validateFields = () => {
    const newErrors: { query?: string; extraInstructions?: string } = {};

    if (!query || query.trim() === '') {
      newErrors.query = 'Query is required';
    }

    if (!extraInstructions || extraInstructions.trim() === '') {
      newErrors.extraInstructions = 'Instructions are required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validateFields()) {
      return;
    }

    const payload = {
      query: query.trim(),
      extra_instructions: extraInstructions.trim(),
      deep_mode: canSeeDeepMode ? deepMode : false,
      visibility,
      tags,
    };

    if (!isCreateMode) {
      onSave({ ...payload, id: template.id });
    } else {
      onSave(payload);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', padding: '16px' }}>
      <div style={{ flex: 1, overflowY: 'auto' }}>
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <div>
            <Text strong>Query *</Text>
            <TextArea
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                if (errors.query) {
                  setErrors({ ...errors, query: undefined });
                }
              }}
              placeholder="Enter the query text..."
              rows={3}
              style={{ marginTop: 4 }}
              status={errors.query ? 'error' : ''}
            />
            {errors.query && (
              <Text type="danger" style={{ fontSize: 12 }}>
                {errors.query}
              </Text>
            )}
          </div>

          <div>
            <Text strong>Instructions *</Text>
            <TextArea
              value={extraInstructions}
              onChange={(e) => {
                setExtraInstructions(e.target.value);
                if (errors.extraInstructions) {
                  setErrors({ ...errors, extraInstructions: undefined });
                }
              }}
              placeholder="Enter the analysis instructions..."
              rows={10}
              style={{ marginTop: 4, fontFamily: 'monospace', fontSize: 13 }}
              status={errors.extraInstructions ? 'error' : ''}
            />
            {errors.extraInstructions && (
              <Text type="danger" style={{ fontSize: 12 }}>
                {errors.extraInstructions}
              </Text>
            )}
          </div>

          <div>
            <div style={{ marginBottom: 4 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Text strong>Deep Analysis Mode</Text>
                <Text type="secondary">[internal]</Text>
                <Tooltip title="Runs a more comprehensive, multi-step analysis. Increases runtime; use only for complex investigations.">
                  <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
                </Tooltip>
              </div>
            </div>
            {canSeeDeepMode ? (
              <>
                <Switch
                  checked={deepMode}
                  onChange={(nextChecked) => {
                    if (!deepMode && nextChecked) {
                      Modal.confirm({
                        title: 'Enable Deep Analysis Mode?',
                        content:
                          'Deep Analysis Mode may take significantly longer to run. Use it only for complex investigations.',
                        okText: 'Enable',
                        cancelText: 'Cancel',
                        onOk: () => setDeepMode(true),
                        onCancel: () => setDeepMode(false),
                      });
                      return;
                    }
                    setDeepMode(Boolean(nextChecked));
                  }}
                />
              </>
            ) : null}
          </div>

          <div>
            <Text strong>Visibility *</Text>
            <Radio.Group
              value={visibility}
              onChange={(e) => setVisibility(e.target.value)}
              style={{ marginTop: 4, display: 'flex', flexDirection: 'column', gap: 8 }}
            >
              <Radio value="private">Private (Only me)</Radio>
              <Radio value="team">Team (Everyone in my organization)</Radio>
              {canManageSysPrompt && <Radio value="recommended">Recommended (All customers)</Radio>}
            </Radio.Group>
          </div>

          <div>
            <Text strong>Tags</Text>
            <div style={{ marginTop: 8 }}>
              <TagInput value={tags} onChange={setTags} maxTags={5} maxTagLength={32} />
            </div>
          </div>
        </Space>
      </div>

      <div
        style={{
          display: 'flex',
          gap: 8,
          paddingTop: 16,
          borderTop: '1px solid #f0f0f0',
          marginTop: 16,
        }}
      >
        <Button onClick={onCancel}>Cancel</Button>
        <Button type="primary" onClick={handleSave} loading={isSaving}>
          {isCreateMode ? 'Create' : 'Update'}
        </Button>
      </div>
    </div>
  );
};

const EmptyState = () => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100%',
      color: '#999',
    }}
  >
    <Text type="secondary">Select an analysis to edit</Text>
  </div>
);

const ManageAnalyses = ({
  open,
  setOpen,
  onRequestOpenManageSchedules,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
  onRequestOpenManageSchedules?: (opts?: { scheduleId?: number }) => void;
}) => {
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
  const [searchText, setSearchText] = useState('');
  const [selectedFilterTags, setSelectedFilterTags] = useState<string[]>([]);
  const [isCreateMode, setIsCreateMode] = useState(false);
  const [scheduleItem, setScheduleItem] = useState<AnalysisTemplate | null>(null);

  const { data = [], isFetching, isError } = useGetAnalysisTemplates({ open });
  const createMutation = useCreateAnalysisTemplate();
  const updateMutation = useUpdateAnalysisTemplate();
  const deleteMutation = useDeleteAnalysisTemplate();

  const selectedTemplate = useMemo(() => {
    if (isCreateMode) {
      return null;
    }
    return selectedTemplateId
      ? data.find((item: AnalysisTemplate) => item.id === selectedTemplateId)
      : null;
  }, [data, selectedTemplateId, isCreateMode]);

  const filteredData = useMemo(() => {
    let filtered = data;

    // Filter by search text (query + tags)
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter((item: AnalysisTemplate) => {
        const queryMatch = item.query.toLowerCase().includes(searchLower);
        const tagsMatch = item.tags?.some((tag) => tag.toLowerCase().includes(searchLower));
        return queryMatch || tagsMatch;
      });
    }

    // Filter by selected tags (match ANY)
    if (selectedFilterTags.length > 0) {
      filtered = filtered.filter((item: AnalysisTemplate) => {
        if (!item.tags || item.tags.length === 0) return false;
        return selectedFilterTags.some((filterTag) =>
          item.tags.some((itemTag) => itemTag.toLowerCase() === filterTag.toLowerCase()),
        );
      });
    }

    return filtered;
  }, [data, searchText, selectedFilterTags]);

  // Get all unique tags from templates for filter UI
  const allTags = useMemo(() => {
    const tagSet = new Set<string>();
    data.forEach((item: AnalysisTemplate) => {
      if (item.tags) {
        item.tags.forEach((tag) => tagSet.add(tag));
      }
    });
    return Array.from(tagSet).sort();
  }, [data]);

  const handleClose = () => {
    setOpen(false);
    setSelectedTemplateId(null);
    setIsCreateMode(false);
    setSearchText('');
    setSelectedFilterTags([]);
    setScheduleItem(null);
  };

  const handleToggleFilterTag = (tag: string) => {
    setSelectedFilterTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag],
    );
  };

  const handleSelectTemplate = (template: AnalysisTemplate) => {
    setIsCreateMode(false);
    setSelectedTemplateId(template.id);
  };

  const handleCreateNew = () => {
    setSelectedTemplateId(null);
    setIsCreateMode(true);
  };

  const handleSave = async (payload: any) => {
    if (isCreateMode) {
      await createMutation.mutateAsync(payload);
      setIsCreateMode(false);
    } else {
      await updateMutation.mutateAsync(payload);
      setSelectedTemplateId(null);
    }
  };

  const handleCancel = () => {
    setSelectedTemplateId(null);
    setIsCreateMode(false);
    setScheduleItem(null);
  };

  const handleDelete = async (id: number) => {
    await deleteMutation.mutateAsync(id);
    if (selectedTemplateId === id) {
      setSelectedTemplateId(null);
    }
  };

  const showForm = isCreateMode || !!selectedTemplate;

  return (
    <>
      <Drawer
        title="Manage Analyses"
        placement="left"
        closable={true}
        onClose={handleClose}
        open={open}
        width={900}
        destroyOnClose={true}
      >
        <div style={{ display: 'flex', height: '100%', gap: 16 }}>
          {/* Left Panel - Analysis List */}
          <div
            style={{
              flex: 1,
              borderRight: '1px solid #f0f0f0',
              paddingRight: 16,
              overflowY: 'auto',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 16,
              }}
            >
              <Title level={5} style={{ margin: 0 }}>
                Analyses
              </Title>
              <Button type="primary" icon={<PlusOutlined />} onClick={handleCreateNew}>
                Create
              </Button>
            </div>
            <Input
              placeholder="Search analyses..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ marginBottom: 12 }}
            />
            {allTags.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <Text type="secondary" style={{ fontSize: 12, marginBottom: 4, display: 'block' }}>
                  Filter by tags:
                </Text>
                <Space size={[4, 4]} wrap>
                  {allTags.map((tag) => (
                    <Tag
                      key={tag}
                      color={selectedFilterTags.includes(tag) ? 'blue' : 'default'}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleToggleFilterTag(tag)}
                    >
                      {tag}
                    </Tag>
                  ))}
                </Space>
                {selectedFilterTags.length > 0 && (
                  <Button
                    type="link"
                    size="small"
                    onClick={() => setSelectedFilterTags([])}
                    style={{ padding: '4px 0', height: 'auto' }}
                  >
                    Clear filters
                  </Button>
                )}
              </div>
            )}
            {isFetching ? (
              <Skeleton active />
            ) : isError ? (
              <Text type="danger">Failed to load analyses</Text>
            ) : filteredData.length === 0 ? (
              <Text type="secondary">No analyses found</Text>
            ) : (
              <List
                size="small"
                dataSource={filteredData}
                renderItem={(item: AnalysisTemplate) => (
                  <AnalysisListItem
                    item={item}
                    onClick={handleSelectTemplate}
                    onDelete={handleDelete}
                    onSchedule={(item: AnalysisTemplate) => setScheduleItem(item)}
                    selected={item.id === selectedTemplateId}
                  />
                )}
              />
            )}
          </div>

          {/* Right Panel - Edit/Create Form */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <Title level={5} style={{ marginBottom: 16 }}>
              {isCreateMode
                ? 'Create Analysis'
                : selectedTemplate
                  ? 'Edit Analysis'
                  : 'Analysis Details'}
            </Title>
            {showForm ? (
              <EditForm
                key={isCreateMode ? 'new' : selectedTemplate?.id}
                template={selectedTemplate}
                onCancel={handleCancel}
                onSave={handleSave}
                isSaving={isCreateMode ? createMutation.isLoading : updateMutation.isLoading}
                isCreateMode={isCreateMode}
              />
            ) : (
              <EmptyState />
            )}
          </div>
        </div>
      </Drawer>
      <ScheduleAnalysisDrawer
        open={!!scheduleItem}
        onClose={handleCancel}
        analysisItem={scheduleItem}
        onRequestOpenManageSchedules={onRequestOpenManageSchedules}
      />
    </>
  );
};

export default ManageAnalyses;
